import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { AlertDelivery } from '@evi/shared';
import { fetchAlertPreference, getDisplayErrorMessage, saveAlertPreference, type AlertPreferenceView } from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * "Choose how often you hear about this site" (Warren, 28 Oct 2026,
 * predictive maintenance stage 4): /alert-preferences?token=..., the link in
 * every alert email to a facilities contact. No sign-in (hoisted above the
 * auth guard like the invite page): the signed link opens this one
 * contact's choice at this one site, and nothing else.
 */
export function AlertPreferencesPage({ onNavigate }: Props) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [view, setView] = useState<AlertPreferenceView | null>(null);
  const [choice, setChoice] = useState<AlertDelivery | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is incomplete. Open it from the email again.'); return; }
    fetchAlertPreference(token)
      .then((v) => { setView(v); setChoice(v.alert_delivery); })
      .catch(async (err: unknown) => setError(await getDisplayErrorMessage(err, 'This link is no longer valid.')));
  }, [token]);

  const save = async (): Promise<void> => {
    if (!choice) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const next = await saveAlertPreference(token, choice);
      setView(next);
      setSaved(true);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not save. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout title={view ? view.location_name : 'Your alerts'} subtitle="How often you hear about this site">
      {!view && !error && <div className={styles.spinnerWrap}><Loader2 size={36} className={styles.spinner} /></div>}
      {error && (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {error}
          </div>
          {!view && <button type="button" className={formStyles.btnSecondary} onClick={() => onNavigate('/login')}>Go to sign in</button>}
        </div>
      )}
      {view && (
        <div className={styles.errorStack}>
          <p>
            Alerts about the EV chargers at <strong>{view.location_name}</strong> for {view.email}. When a charger keeps
            losing its connection you hear at most once a week, unless you choose Off.
          </p>
          <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <legend style={{ fontWeight: 600, marginBottom: 6 }}>When a charger goes offline</legend>
            {view.choices.map((c) => (
              <label key={c.value} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 32 }}>
                <input type="radio" name="alert_delivery" value={c.value} checked={choice === c.value} onChange={() => { setChoice(c.value); setSaved(false); }} />
                {c.label}
              </label>
            ))}
          </fieldset>
          <button type="button" className={formStyles.submitBtn} disabled={saving || !choice || choice === view.alert_delivery} onClick={() => { void save(); }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && <p role="status">Saved. This applies from the next alert.</p>}
        </div>
      )}
    </AuthLayout>
  );
}

export default AlertPreferencesPage;
